import { Injectable } from '@angular/core';
import { HumanInterface } from '../engine/models/human.interface';

declare const Human: HumanInterface;

// Bytes (for Geometry)
enum uncompressedBytesPer {
  Positions = 4,
  Normals = 4,
  UVs = 4,
}

enum compressedBytesPer {
  Positions = 2,
  Normals = 2 / 3,
  UVs = 2,
}

@Injectable({
  providedIn: 'root',
})
export class GeometryAssetMemoryService {
  constructor() {}

  /* Calculates geometry sizes in BYTES
   * NOTE: The compressed size of the index array depends on the size of the array
   */
  getBytesPerIndex(indices, compressed) {
    if (indices.length < Math.pow(2, 8)) {
      return 1; // 1 BYTE = 8 bits
    } else if (indices.length < Math.pow(2, 16)) {
      return 2; // 2 BYTES = 2*8 bits = 16 bits
    } else if (indices.length < Math.pow(2, 32)) {
      return 4; // 4 BYTES = 4*8 bits = 32 bits
    }
  }

  getAsset(geometryId) {
    return Human.assets.geometries.geometries[geometryId];
  }

  getPositions(geometryId) {
    return this.getAsset(geometryId).geometry._core.arrays.positions;
  }

  getNormals(geometryId) {
    return this.getAsset(geometryId).geometry._core.arrays.positions;
  }

  getIndices(geometryId) {
    return this.getAsset(geometryId).geometry._core.arrays.indices;
  }

  hasUVs(geometryId) {
    return this.getAsset(geometryId).geometry._core.arrays.hasOwnProperty('uvs');
  }

  // NOTE: Assumes at most one UV set
  getUVs(geometryId) {
    if (this.hasUVs(geometryId)) {
      return this.getAsset(geometryId).geometry._core.arrays.uvs[0];
    } else {
      return [];
    }
  }

  // NOTE: Assumes that if one resource is compressed, then all of them are.
  isCompressed(geometryId) {
    return this.getAsset(geometryId).compressed;
  }

  calculatePositions(geometryId, compressed) {
    const positions = this.getPositions(geometryId);
    const bytesper = compressed ? compressedBytesPer.Positions : uncompressedBytesPer.Positions;
    return positions.length * bytesper;
  }

  calculateIndices(geometryId, compressed) {
    const indices = this.getIndices(geometryId);
    const bytesper = this.getBytesPerIndex(indices, compressed);
    return indices.length * bytesper;
  }

  calculateNormals(geometryId, compressed) {
    const normals = this.getNormals(geometryId);
    const bytesper = compressed ? compressedBytesPer.Normals : uncompressedBytesPer.Normals;
    return Math.floor(normals.length * bytesper);
  }

  calculateUVs(geometryId, compressed) {
    const uvs = this.getUVs(geometryId);
    const bytesper = compressed ? compressedBytesPer.UVs : uncompressedBytesPer.UVs;
    return uvs.length * bytesper;
  }

  calculateGeometry(geometryId, compressed) {
    let sum = 0;
    sum += this.calculateIndices(geometryId, compressed);
    sum += this.calculatePositions(geometryId, compressed);
    sum += this.calculateNormals(geometryId, compressed);
    sum += this.calculateUVs(geometryId, compressed);
    return sum;
  }

  getTris(geometryId) {
    return this.getIndices(geometryId).length / 3;
  }

  getVerts(geometryId) {
    return this.getPositions(geometryId).length / 3;
  }

  getGeometryMemory(geometryId) {
    const gc = this.getAsset(geometryId).geometry._core;
    const vBytes = gc.vertexBuf.itemByteSize * gc.vertexBuf.length;
    const tBytes = gc.indexBuf.itemByteSize * gc.indexBuf.length;
    const nBytes = gc.normalBuf.itemByteSize * gc.normalBuf.length;

    let uvBytes = 0.0;
    if (!!gc.uvBufs) {
      for (let i = 0; i < gc.uvBufs.length; i++) {
        uvBytes += gc.uvBufs[i].itemByteSize * gc.uvBufs[i].length;
      }
    }

    return vBytes + tBytes + nBytes + uvBytes;
  }

  getStat(geometryId) {
    return {
      id: geometryId,
      tris: this.getTris(geometryId),
      verts: this.getVerts(geometryId),
      memory: this.getGeometryMemory(geometryId),
    };
  }
}
